<!-- src/views/reviews/UserReviewsListView.vue -->
<template>
  <div class="p-4 sm:p-6 md:p-8 text-left">
    <div class="max-w-7xl mx-auto space-y-6">
      
      <div class="flex items-center justify-between">
        <!-- 🛡️ ALIGNED HEADING STYLE -->
        <h1 class="text-2xl sm:text-3xl font-black text-[#0A1630] uppercase tracking-tight">Reviews for {{ targetUserName }}</h1>
        <button @click="goBack" class="text-xs font-black uppercase tracking-widest text-[#0A1630] hover:text-[#20FCF1] transition-colors bg-transparent border-none cursor-pointer">
          &larr; Back to Profile
        </button>
      </div>

      <!-- Stats Summary -->
      <div v-if="!loading && reviews.length > 0" class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div class="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 border-l-4 border-l-[#20FCF1]">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest">Total Reviews</p>
          <p class="text-3xl font-black text-[#0A1630] mt-1">{{ reviews.length }}</p>
        </div>
        <div class="bg-white p-6 rounded-[32px] shadow-sm border border-slate-100 border-l-4 border-l-[#FD5E00]">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest">Average Rating</p>
          <div class="flex items-center mt-1">
            <span class="text-3xl font-black text-[#0A1630] mr-2">{{ averageRating }}</span>
            <StarIcon class="h-6 w-6 text-amber-500 fill-amber-400" />
          </div>
        </div>
      </div>

      <!-- Loading / Error / Empty States -->
      <div v-if="loading" class="text-center py-20 bg-white rounded-[40px] shadow-sm border border-slate-100">
        <div class="animate-spin h-8 w-8 border-2 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-3"></div>
        <p class="text-[8px] font-black uppercase tracking-widest text-slate-400">Loading reviews...</p>
      </div>
      
      <div v-else-if="error" class="bg-rose-50 border border-rose-100 text-rose-700 p-6 rounded-[32px] text-center font-black uppercase tracking-widest">
        <p class="font-black text-rose-800">Error Loading Reviews</p>
        <p class="text-rose-600 text-xs mt-1">{{ error }}</p>
      </div>

      <div v-else-if="reviews.length === 0" class="text-center py-20 bg-white rounded-[40px] shadow-sm border border-slate-100">
        <StarIcon class="mx-auto h-12 w-12 text-slate-200 mb-4" />
        <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-tight">No reviews found</h3>
        <p class="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">This user has not received any reviews yet.</p>
      </div>

      <!-- Reviews List -->
      <div v-else class="space-y-4">
        <!-- 🛡️ CLICKABLE ROUTING: Wraps each review item to launch completedForm popup on click -->
        <router-link 
          v-for="review in reviews" 
          :key="review.id" 
          :to="{ name: 'completedForm', params: { collectionName: 'user_reviews', submissionId: review.id } }"
          class="bg-white shadow-sm border border-slate-100 rounded-[32px] p-8 transition duration-150 hover:shadow-md hover:border-[#20FCF1]/30 block no-underline cursor-pointer"
        >
          <div class="flex items-start justify-between">
            <div class="text-left">
              <div class="flex items-center text-left">
                <!-- 🛡️ DYNAMIC PROFILE RESOLVER (Issue #1): Reads correct nested reviewer name -->
                <h3 class="text-lg font-black text-[#0A1630] uppercase tracking-tight">{{ review.formData?.reviewerName || 'Site Manager' }}</h3>
                <span class="mx-2 text-slate-300">&bull;</span>
                <span class="text-xs font-bold text-slate-400 uppercase tracking-widest">{{ formatDate(review.formData?.submittedAt || review.submittedAt || review.date) }}</span>
              </div>
              <div class="mt-1 flex items-center">
                <!-- 🛡️ DYNAMIC STAR RATING RESOLVER: Reads correct nested rating -->
                <StarIcon 
                  v-for="i in 5" 
                  :key="i" 
                  class="h-5 w-5" 
                  :class="i <= (review.formData?.rating || 0) ? 'text-amber-500 fill-amber-400' : 'text-slate-200'" 
                />
              </div>
              <p v-if="review.formData?.siteName || review.siteName" class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest mt-2">
                Site: {{ review.formData?.siteName || review.siteName }}
              </p>
            </div>
          </div>
          
          <div v-if="review.formData?.highlights && review.formData.highlights.length > 0" class="text-left mt-4">
            <div class="flex flex-wrap gap-2 text-left">
              <span 
                v-for="highlight in review.formData.highlights" 
                :key="highlight"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
              >
                {{ highlight }}
              </span>
            </div>
          </div>

          <!-- 🛡️ DYNAMIC NESTED COMMENTS RESOLVER -->
          <div v-if="review.formData?.comments" class="mt-4 text-slate-700 bg-slate-50 p-5 rounded-2xl border border-slate-100 text-sm leading-relaxed italic text-left">
            "{{ review.formData.comments }}"
          </div>
        </router-link>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { StarIcon } from '@heroicons/vue/24/solid';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const reviews = ref([]);
const loading = ref(true);
const error = ref('');
const targetUserName = ref('User');

const targetUserId = computed(() => route.params.userId);

const averageRating = computed(() => {
  if (reviews.value.length === 0) return 0;
  const sum = reviews.value.reduce((acc, review) => acc + (review.formData?.rating || 0), 0);
  return (sum / reviews.value.length).toFixed(1);
});

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const goBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push('/dashboard');
  }
};

const fetchTargetUserName = async (userId) => {
    if (!userId) return;
    try {
        const userDoc = await getDoc(doc(db, 'userProfiles', userId));
        if (userDoc.exists()) {
            const data = userDoc.data();
            targetUserName.value = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'User';
        }
    } catch (e) {
        console.error("Error fetching user name:", e);
    }
};

const fetchReviews = async (userId) => {
  if (!userId) return;
  loading.value = true;
  error.value = '';
  try {
    await fetchTargetUserName(userId);

    // 🛡️ RE-ALIGNED GATING: Correctly queries the nested 'formData.targetUserId'
    const q = query(
      collection(db, 'user_reviews'), 
      where('formData.targetUserId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    
    const fetchedReviews = querySnapshot.docs.map(docSub => ({
      id: docSub.id,
      ...docSub.data()
    }));

    // Client-side sort descending
    reviews.value = fetchedReviews.sort((a, b) => {
      const dateA = a.formData?.submittedAt?.toDate ? a.formData.submittedAt.toDate() : new Date(a.formData?.submittedAt || a.submittedAt || 0);
      const dateB = b.formData?.submittedAt?.toDate ? b.formData.submittedAt.toDate() : new Date(b.formData?.submittedAt || b.submittedAt || 0);
      return dateB - dateA; 
    });

  } catch (err) {
    console.error('Error fetching reviews:', err);
    error.value = 'Could not load reviews.';
  } finally {
    loading.value = false;
  }
};

// 🛡️ REACTIVE ROUTE WATCHER: Bypasses mounting race conditions completely
watch(targetUserId, (newId) => {
  if (newId) {
    fetchReviews(newId);
  }
}, { immediate: true });
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>